import { useEffect, useState } from "react";

import type { PopularTopic } from "./popular-questions";

/**
 * Fetches the demand-ranked topic list (GET /popular-topics — aggregates only,
 * no counts, no raw text) once on mount. Fails soft: on any error it returns an
 * empty list, and the home falls back to the curated default order. The proxy
 * caches the result, so this is a cheap request.
 */
export function usePopularTopics(): readonly PopularTopic[] {
  const [topics, setTopics] = useState<readonly PopularTopic[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("/popular-topics", { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((data: { topics?: unknown }) => {
        if (cancelled) return;
        const list = Array.isArray(data.topics) ? data.topics : [];
        const clean = list.filter(
          (topic): topic is PopularTopic =>
            typeof topic === "object" &&
            topic !== null &&
            typeof (topic as PopularTopic).book === "string" &&
            typeof (topic as PopularTopic).chapter === "number",
        );
        setTopics(clean);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return topics;
}
